/**
 * Kilometerstand-registratie (FO-157, batch-31) — gedeeld tussen API (DTO +
 * Zod-input) en de portal/backoffice-UI.
 *
 * De bestuurder geeft periodiek (km-herinnering-push) de actuele kilometerstand
 * van zijn voertuig door via de portal; backoffice kan een stand registreren en
 * ziet de historie op het voertuig-detail. De laatste registratie werkt de
 * cache `Voertuig.kilometerstand` bij.
 *
 * Gate-keys: portal (eigen bestuurder) = `VOERTUIGEN` canView + portal-access;
 * backoffice-registratie = `VOERTUIGEN` canEdit, inzage = canView.
 */

import { z } from 'zod'

// ─── Enum (string-union, gelijk aan de Prisma-enum) ──────────────────────────

// batch-37 onderhoudsrapport (FO-327): ONDERHOUDSRAPPORT = rapport-odometer uit
// de dagelijkse telematica-PDF (systeembron; alleen de rapport-ingest schrijft 'm).
// batch-38 garagefacturen (FO-329): GARAGEFACTUUR = km-stand van een garagefactuur
// (systeembron; alleen de onderhoud-registratie-service schrijft 'm).
// TELEMATICA = stand uit het wekelijkse km-rapport van de tracker
// (systeembron; alleen de rapport-ingest schrijft 'm).
export const VOERTUIG_KM_BRON = [
  'PORTAL',
  'BACKOFFICE',
  'ONDERHOUDSRAPPORT',
  'GARAGEFACTUUR',
  'TELEMATICA',
  // batch-70 wagenpark-week (FO-528 §A2) - de wekelijkse handmatige import van het
  // telematica-exportbestand (systeembron; alleen de km-import schrijft 'm).
  'IMPORT',
] as const
export type VoertuigKmBron = (typeof VOERTUIG_KM_BRON)[number]

export const VOERTUIG_KM_BRON_LABELS: Record<VoertuigKmBron, string> = {
  PORTAL: 'Bestuurder (app)',
  BACKOFFICE: 'Backoffice',
  ONDERHOUDSRAPPORT: 'Onderhoudsrapport',
  GARAGEFACTUUR: 'Garagefactuur',
  TELEMATICA: 'Km-rapport tracker',
  IMPORT: 'Telematica-import',
}

// ─── Validatie-grenzen (gelijk aan de bestaande km-validatie in voertuigen.ts) ─

export const KM_STAND_MIN = 0
export const KM_STAND_MAX = 2_000_000

// ─── Zod request-body schema + input type ────────────────────────────────────

// Create: de bestuurder/backoffice geeft alleen de stand door; het voertuig
// wordt server-side afgeleid (portal: eigen actieve toewijzing; backoffice:
// :id-param). `bevestigLagereStand` is de expliciete bevestiging wanneer de
// nieuwe stand lager is dan de laatst bekende (een teller loopt niet terug;
// een bevestigde lagere stand corrigeert een eerdere typefout). Zonder die
// bevestiging antwoordt de API 409 `KM_LAGER_DAN_LAATSTE` met de laatst
// bekende stand in de details — de UI toont dan een waarschuwing, geen blokkade.
export const createVoertuigKmRegistratieSchema = z.object({
  kmStand: z.number().int().min(KM_STAND_MIN).max(KM_STAND_MAX),
  bevestigLagereStand: z.boolean().optional(),
})
export type CreateVoertuigKmRegistratieInput = z.infer<
  typeof createVoertuigKmRegistratieSchema
>

// ─── DTO's ───────────────────────────────────────────────────────────────────

export interface VoertuigKmRegistratieDTO {
  id: string
  voertuigId: string
  /** Kenteken van het voertuig (leesbaar in lijsten). */
  kenteken: string | null
  /** Bestuurder op wie de stand betrekking heeft; null bij een backoffice-registratie zonder actieve bestuurder. */
  employeeId: string | null
  bestuurderNaam: string | null
  kmStand: number
  /** ISO datetime van de registratie. */
  datum: string
  bron: VoertuigKmBron
  /** Naam van de user die de registratie invoerde (audit), of null. */
  aangemaaktDoorNaam: string | null
  createdAt: string
}

/** Foutdetails van de 409 `KM_LAGER_DAN_LAATSTE`-respons (plausibiliteitscheck). */
export interface KmLagerDanLaatsteDetails {
  laatsteKmStand: number
  /** ISO datetime van de laatst bekende registratie, of null (alleen cache-waarde bekend). */
  laatsteDatum: string | null
}
